import type { Service } from "@mirage/service-fss-bridge/service";
import provideFSSBridgeService from "@mirage/service-fss-bridge/service";
import { isEmbedPage } from "@mirage/shared/util/tiny-utils";

import type {
  ConversationEvent,
  FSSActionEvent,
  FSSMessageEvent,
  UnavailableSource,
} from "@mirage/service-fss-bridge/service/bridge_types";
import type { Observable } from "rxjs";
import { BehaviorSubject, of } from "rxjs";
import {
  createIframeMessageChannel,
  type BridgeMessageChannel,
} from "./BridgeMessageChannel";

export class FSSBridgeAdapter implements Service {
  constructor(private channel: BridgeMessageChannel) {
    this.channel.addListener(this.handleMessage);
  }
  openFilePreview: (fileID: string) => Promise<void>;

  private conversationEventSubject = new BehaviorSubject<
    ConversationEvent | undefined
  >(undefined);
  private actionEventSubject = new BehaviorSubject<FSSActionEvent | undefined>(
    undefined,
  );

  private handleMessage = (eventMessage: FSSMessageEvent) => {
    switch (eventMessage.eventType) {
      case "conversation":
        this.conversationEventSubject.next(eventMessage);
        break;
      case "action":
        this.actionEventSubject.next(eventMessage);
        break;
      default: {
        // This will cause a type error when new event types are added and are not handled above.
        const exhaustiveCheck: never = eventMessage;
        return exhaustiveCheck;
      }
    }
  };

  public readyPage = async () => {
    this.channel.postMessage({
      eventType: "action",
      source: "ready.page",
    });
  };

  public openAddSourceModal = async () => {
    this.channel.postMessage({
      eventType: "action",
      source: "open.source-picker",
    });
  };

  public updateConversation = async (conversationID: string) => {
    this.channel.postMessage({
      eventType: "action",
      source: "update.conversation",
      data: {
        conversationId: conversationID,
      },
    });
  };

  public closeSidebar = async () => {
    this.channel.postMessage({
      eventType: "action",
      source: "close.sidebar",
    });
  };

  public openSessionHistory = async () => {
    this.channel.postMessage({
      eventType: "action",
      source: "open.session-history",
    });
  };

  public notifyLogin = async (uid: number) => {
    this.channel.postMessage({
      eventType: "action",
      source: "notify.login",
      data: {
        userId: uid,
      },
    });
  };

  public notifyLogout = async () => {
    this.channel.postMessage({
      eventType: "action",
      source: "notify.logout",
    });
  };

  public openUnavailableSources = async (
    unavailableSources: UnavailableSource[],
  ) => {
    this.channel.postMessage({
      eventType: "action",
      source: "open.unavailable-sources",
      data: {
        unavailableSources,
      },
    });
  };

  // New event-based observables
  public observeConversationEvents = (): Observable<
    ConversationEvent | undefined
  > => {
    return this.conversationEventSubject;
  };

  public observeActionEvents = (): Observable<FSSActionEvent | undefined> => {
    return this.actionEventSubject;
  };

  public isSupportedEnv = () => {
    return true;
  };
}

function createNoopService(): Service {
  const noop = async () => {};
  const throwError = () => {
    throw new Error("FSS Bridge is not available in this environment");
  };

  return {
    isSupportedEnv: () => false,
    readyPage: noop,
    openAddSourceModal: throwError,
    updateConversation: throwError,
    closeSidebar: throwError,
    openSessionHistory: throwError,
    notifyLogin: noop,
    notifyLogout: noop,
    openUnavailableSources: noop,
    observeConversationEvents: () => of(undefined),
    observeActionEvents: () => of(undefined),
  };
}

export function setupFSSBridgeHandlers() {
  let service: Service;
  if (!isEmbedPage()) {
    // set up a Service that no-ops when Dash web is not in an iframe
    service = createNoopService();
  } else {
    service = new FSSBridgeAdapter(createIframeMessageChannel());
  }
  provideFSSBridgeService(service);
}
